import {announce} from '@github-ui/aria-live'
import {PercentageCircle} from '@github-ui/percentage-circle'
import {useQuery} from '@github-ui/react-query'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {ClockIcon} from '@primer/octicons-react'
import {useEffect, useRef} from 'react'

import {
  fetchIssuesBulkJobStatus,
  REPO_ISSUES_BULK_UPDATE_TIMEOUT_MESSAGE,
  REPO_ISSUES_BULK_UPDATE_TIMEOUT_MS,
  type RepoIssuesBulkUpdateResult,
} from '../helpers/repo-issues-bulk-update'

const INITIAL_POLL_INTERVAL_MS = 1000
const MAX_POLL_INTERVAL_MS = 60_000

interface RepoIssuesBulkJobInfoProps {
  jobId: string | null
  enabled: boolean
  clearJobId: () => void
  refreshIssues: () => Promise<{isError: boolean; error: unknown}>
  onCompleted?: (jobId: string, result: RepoIssuesBulkUpdateResult) => boolean
  onError?: (jobId: string, error: Error) => boolean
}

export function RepoIssuesBulkJobInfo({
  jobId,
  enabled,
  clearJobId,
  refreshIssues,
  onCompleted,
  onError,
}: RepoIssuesBulkJobInfoProps) {
  const {addPersistedToast, addToast, clearPersistedToast} = useToastContext()
  const handledJobIds = useRef(new Set<string>())

  const jobQuery = useQuery({
    enabled: Boolean(jobId) && enabled,
    queryKey: ['repo-issues-bulk-job', jobId],
    queryFn: ({signal}) => fetchIssuesBulkJobStatus({jobId: jobId ?? '', signal}),
    // Keep checking because a temporary network error does not mean the job stopped.
    retry: true,
    // Slow down repeated checks, and let the timeout below stop jobs that never finish.
    refetchInterval: query => {
      if (
        query.state.data?.status === 'completed' ||
        query.state.data?.status === 'failed' ||
        query.state.data?.status === 'missing'
      ) {
        return false
      }
      return Math.min(
        INITIAL_POLL_INTERVAL_MS * 1.5 ** Math.max(query.state.dataUpdateCount - 1, 0),
        MAX_POLL_INTERVAL_MS,
      )
    },
  })

  useEffect(() => {
    if (!jobId || !enabled || handledJobIds.current.has(jobId)) return

    const timeout = setTimeout(() => {
      handledJobIds.current.add(jobId)
      if (onError?.(jobId, new Error(REPO_ISSUES_BULK_UPDATE_TIMEOUT_MESSAGE))) return

      clearJobId()
      clearPersistedToast()
      // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
      addToast({type: 'error', message: REPO_ISSUES_BULK_UPDATE_TIMEOUT_MESSAGE})
      announce(REPO_ISSUES_BULK_UPDATE_TIMEOUT_MESSAGE, {assertive: true})
    }, REPO_ISSUES_BULK_UPDATE_TIMEOUT_MS)

    return () => clearTimeout(timeout)
  }, [addToast, clearJobId, clearPersistedToast, enabled, jobId, onError])

  useEffect(() => {
    if (!jobId || !enabled || handledJobIds.current.has(jobId)) return

    const status = jobQuery.data
    if (!status) return
    if (status.status === 'missing' || status.status === 'failed') {
      handledJobIds.current.add(jobId)
      const message = status.status === 'failed' ? status.errorMessage : 'Bulk update had an error'
      if (onError?.(jobId, new Error(message))) return

      clearJobId()
      clearPersistedToast()
      // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
      addToast({type: 'error', message})
      return
    }

    if (status.status === 'pending') {
      if (status.progress.state === 'STARTED') {
        const percentage = Math.min(Math.max(status.progress.percentage, 0), 100)
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addPersistedToast({
          message: `Bulk update is in progress ${percentage} % ${
            status.progress.failedCount > 0 ? `(${status.progress.failedCount} issues failing to update)` : ''
          }`,
          icon: <PercentageCircle progress={percentage / 100} />,
          type: 'info',
          role: 'status',
        })
      } else {
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addPersistedToast({message: 'Bulk update is pending', icon: <ClockIcon />, type: 'info', role: 'status'})
      }
      return
    }

    handledJobIds.current.add(jobId)
    if (onCompleted?.(jobId, status.result)) return

    void (async () => {
      let refreshFailed = false
      try {
        const refresh = await refreshIssues()
        refreshFailed = refresh.isError
      } catch {
        refreshFailed = true
      }
      clearJobId()
      clearPersistedToast()
      if (refreshFailed) {
        const message = 'Issues updated, but the results could not be refreshed'
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({type: 'error', message})
        announce(message, {assertive: true})
        return
      }

      const failedCount = status.result.failedIssueNodeIds.length
      const message = `Bulk update completed ${failedCount > 0 ? 'with errors' : 'successfully'}`
      // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
      addToast({type: 'success', message})
      announce(message)
    })()
  }, [
    addPersistedToast,
    addToast,
    jobId,
    enabled,
    clearPersistedToast,
    clearJobId,
    jobQuery.data,
    onCompleted,
    onError,
    refreshIssues,
  ])

  return null
}
